import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Star, Briefcase } from 'lucide-react';
import { GlowingCTA } from '../ui/GlowingCTA';
import api from '../../services/api';

const fallbackPackages = [
  {
    _id: '1',
    slug: 'start-business',
    name: 'Start Business',
    description: 'Everything you need to register and start your business legally.',
    basePrice: 4999,
    originalPrice: 7999,
    isPopular: false,
    savings: 38,
    features: ['Pvt Ltd Registration', 'GST Registration', 'Udyam Certificate', 'Current Account Support', 'Business PAN & TAN'],
  },
  {
    _id: '2',
    slug: 'compliance-care',
    name: 'Compliance Pro',
    description: 'Full compliance & tax management so you can focus on growth.',
    basePrice: 9999,
    originalPrice: 15999,
    isPopular: true,
    savings: 38,
    features: ['Annual ROC Filings', 'Monthly GST Returns', 'Income Tax Return', 'Dedicated CA Manager', 'Compliance Calendar', 'Priority Support'],
  },
  {
    _id: '3',
    slug: 'digital-growth',
    name: 'Digital Growth',
    description: 'Take your MSME digital and start generating leads.',
    basePrice: 14999,
    originalPrice: 24999,
    isPopular: false,
    savings: 40,
    features: ['5-Page Business Website', 'Google My Business', 'SEO Optimization', '3 Month Social Media', 'WhatsApp Business Setup', 'Analytics Dashboard'],
  },
];

function PackageCard({ pkg, index }) {
  const [flipped, setFlipped] = useState(false);
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15, duration: 0.6 }}
      className="relative h-[600px] sm:h-[560px] lg:h-[500px] perspective-1000 cursor-pointer"
      onClick={() => setFlipped(!flipped)}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
        className="relative w-full h-full"
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* FRONT */}
        <div
          className={`absolute inset-0 backface-hidden rounded-3xl ${
            pkg.isPopular
              ? 'bg-gradient-to-br from-blue-600/90 to-cyan-600/80 border-2 border-cyan-400/50 shadow-lg dark:shadow-[0_0_40px_rgba(6,182,212,0.3)]'
              : 'bg-white dark:bg-[#1A2A4A] backdrop-blur-xl border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none'
          }`}
        >
          {pkg.isPopular && (
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 w-max max-w-[90%] px-4 sm:px-5 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black text-[11px] sm:text-xs font-black flex items-center justify-center gap-1.5 shadow-lg shadow-amber-500/50 uppercase tracking-wider whitespace-nowrap">
              <Star className="w-4 h-4 flex-shrink-0" /> Most Popular
            </div>
          )}
          <div className="absolute inset-0 rounded-3xl overflow-hidden p-6 sm:p-8 flex flex-col">
            <div className="mb-2">
              <h3 className={`text-2xl font-black font-heading tracking-tight ${pkg.isPopular ? 'text-white drop-shadow-sm' : 'text-slate-900 dark:text-white'}`}>{pkg.name}</h3>
              <p className={`text-sm mt-3 leading-relaxed font-medium line-clamp-2 ${pkg.isPopular ? 'text-white/90' : 'text-slate-600 dark:text-slate-300'}`}>{pkg.description}</p>
            </div>
            <ul className="mt-6 space-y-3 flex-1">
              {pkg.features.slice(0, 4).map((f, i) => (
                <li key={i} className={`flex items-center gap-3 text-sm font-medium ${pkg.isPopular ? 'text-white/95' : 'text-slate-700 dark:text-slate-200'}`}>
                  <CheckCircle className={`w-4 h-4 flex-shrink-0 ${pkg.isPopular ? 'text-amber-300' : 'text-cyan-500 dark:text-cyan-400'}`} />
                  {f}
                </li>
              ))}
              {pkg.features.length > 4 && (
                <li className={`text-sm pl-7 font-medium ${pkg.isPopular ? 'text-white/75' : 'text-slate-600 dark:text-slate-400'}`}>+{pkg.features.length - 4} more included...</li>
              )}
            </ul>
            <div className={`mt-6 text-center text-xs font-bold tracking-wide ${pkg.isPopular ? 'text-white/80' : 'text-slate-500 dark:text-slate-500'}`}>TAP TO SEE ALL FEATURES →</div>
          </div>
        </div>

        {/* BACK */}
        <div
          className="absolute inset-0 backface-hidden rounded-3xl p-6 sm:p-8 flex flex-col overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-700 dark:from-indigo-700/95 dark:to-purple-800/95 border border-slate-200 dark:border-[#1E2A3A] backdrop-blur-xl shadow-lg"
          style={{ transform: 'rotateY(180deg)' }}
        >
          <h4 className="text-xl font-black text-white font-heading mb-6 tracking-tight">✓ Everything Included</h4>
          <ul className="space-y-3 flex-1">
            {pkg.features.map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-white/90 text-sm font-medium">
                <CheckCircle className="w-5 h-5 text-amber-300 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/packages/${pkg.slug}`); }}
            className="mt-6 w-full py-4 rounded-2xl bg-white text-blue-700 font-black text-sm hover:bg-amber-50 transition-colors shadow-lg"
          >
            Get {pkg.name} →
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function PackagesPreview() {
  const [packages, setPackages] = useState(fallbackPackages);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await api.get('/api/packages');
        const fetched = res.data?.packages || [];
        if (fetched.length > 0) {
          setPackages(fetched.slice(0, 3));
        }
      } catch {
        // keep fallback
      }
    };
    fetchPackages();
  }, []);

  return (
    <section className="py-24 bg-gradient-to-b from-white via-blue-50/20 to-slate-50 dark:from-[#0A1628] dark:via-blue-950/10 dark:to-[#0d1e3a] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-cyan-500/25 text-cyan-600 dark:text-cyan-400 text-sm font-extrabold mb-6 shadow-sm backdrop-blur-md">
            <Briefcase className="w-4 h-4" /> Business Packages
          </div>
          <h2 className="text-4xl md:text-5xl font-black font-heading mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-500 dark:from-blue-400 dark:via-purple-400 dark:to-cyan-400">
            Save More, Do More
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mt-4 text-lg max-w-2xl mx-auto font-medium">
            Bundled packages crafted for every stage of your business journey. Click any card to see all features.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {packages.map((pkg, i) => (
            <PackageCard key={pkg._id || i} pkg={pkg} index={i} />
          ))}
        </div>

        <div className="text-center mt-12">
          <GlowingCTA onClick={() => navigate('/packages')} className="px-10 py-4">
            Compare All Packages <ArrowRight className="w-5 h-5 ml-2 inline" />
          </GlowingCTA>
        </div>
      </div>
    </section>
  );
}
